import { useState, useEffect, useCallback } from "react";

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const useTradeDelay = (execute) => {

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [releaseStatuses, setReleaseStatuses] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchOrders = useCallback(async () => {
    if (!execute) return;
    setLoading(true);
    try {
      const data = await execute("GET", `${BASE_URL}/api/tradeDelay/orders`);
      setOrders(data || []);
    } catch (err) {
      showSnackbar("שגיאה בטעינת הזמנות", "error");
    } finally {
      setLoading(false);
    }
  }, [execute]);

  const fetchReleaseStatuses = useCallback(async () => {
    if (!execute) return;
    try {
      const data = await execute(
        "GET",
        `${BASE_URL}/api/tradeDelay/releaseStatuses`,
      );
      setReleaseStatuses(data || []);
    } catch {
      // non-critical
    }
  }, [execute]);

  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null); // { toRelease: [], skipped: [] }
  const [releasing, setReleasing] = useState(false);
  const [releaseResult, setReleaseResult] = useState(null); // { released: [], failed: [] }

  const updateOrderStatus = async (ordname, newStatus) => {
    await execute(
      "PATCH",
      `${BASE_URL}/api/tradeDelay/orders/${ordname}/status`,
      { newStatus },
    );
    showSnackbar(`סטטוס הזמנה ${ordname} עודכן בהצלחה`);
    await fetchOrders();
  };

  const fetchLastSales = async (custName, partName, skip = 0) => {
    const params = new URLSearchParams({ custName, partName, skip });
    const data = await execute(
      "GET",
      `${BASE_URL}/api/tradeDelay/lastSales?${params}`,
    );
    return data || [];
  };

  // עדכון שדות פריט הזמנה ב-Priority
  const updateOrderItem = async (ordname, kline, updates) => {
    await execute(
      "PATCH",
      `${BASE_URL}/api/tradeDelay/orders/${ordname}/items/${kline}`,
      updates,
    );
  };

  // שלב 1: סריקה בלבד
  const runPreview = async () => {
    setPreviewing(true);
    try {
      const result = await execute(
        "GET",
        `${BASE_URL}/api/tradeDelay/autoRelease/preview`,
      );
      setPreviewData(result);
    } catch {
      showSnackbar("שגיאה בסריקת הזמנות", "error");
    } finally {
      setPreviewing(false);
    }
  };

  // שלב 2: שחרור הזמנות נבחרות
  const confirmRelease = async (selected) => {
    setReleasing(true);
    try {
      const result = await execute(
        "POST",
        `${BASE_URL}/api/tradeDelay/autoRelease`,
        { selected },
      );
      setPreviewData(null);
      setReleaseResult(result);
      await fetchOrders();
    } catch {
      showSnackbar("שגיאה בשחרור הזמנות", "error");
    } finally {
      setReleasing(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchReleaseStatuses();
  }, [fetchOrders, fetchReleaseStatuses]);

  return {
    orders,
    loading,
    releaseStatuses,
    snackbar,
    setSnackbar,
    updateOrderStatus,
    updateOrderItem,
    refreshOrders: fetchOrders,
    fetchLastSales,
    previewing,
    previewData,
    setPreviewData,
    runPreview,
    releasing,
    releaseResult,
    setReleaseResult,
    confirmRelease,
  };
};

export default useTradeDelay;
